import React from 'react';
import {
  Draggable,
} from 'react-beautiful-dnd';

import {
  Icon,
  Tooltip,
  Dropdown,
  Checkbox,
  ExerciseProperties,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import ExerciseOrder from './ExerciseOrder';
import Alternatives from './Alternatives';

import * as S from './styled';

type Item = {
  id: number,
  name: string,
  note: string,
  selected: boolean,
};

type Props = {
  index: number,
  readOnly: boolean,
  exercise: Item,
  number: string,
  numbers: string[],
  properties: any[],
  prevNumber: string,
  nextNumber: string,
  visibleSelect: boolean,
  isFirstAlternative: boolean,

  onSelect: { (): void },
  onUpdate: { (): void },
  onRequestUpdate: { (): void },
  onRequestDelete: { (): void },
  onRequestReplace: { (): void },
  onRequestSetOrderName: { (): void },

  onRequestAddProperty: { (): void },
  onRequestDropProperty: { (): void },
  onRequestSaveProperty: { (): void },
  onRequestDeleteProperty: { (): void },
  onRequestAddAlternative: { (exerciseId: number): void },
  onRequestSetAlternative: { (): void },
  onRequestRemoveAlternative: { (): void },
};

const tooltipText = {
  exerciseProperty: 'Hover over exercises and click this button to start adding exercise properties.',
};

const WorkoutExercise = ({
  index,
  number,
  numbers,
  exercise,
  readOnly,
  nextNumber,
  prevNumber,
  properties,
  visibleSelect,

  onSelect,
  onRequestUpdate,
  onRequestDelete,
  onRequestReplace,
  onRequestSetOrderName,

  onRequestAddProperty,
  onRequestSaveProperty,
  onRequestDeleteProperty,
  onRequestAddAlternative,
  onRequestSetAlternative,
  onRequestRemoveAlternative,
}: Props) => {
  const actions = [
    {
      name: 'Add alternative',
      onClick: () => onRequestAddAlternative(exercise.id),
    },
    {
      name: 'Replace',
      onClick: () => onRequestReplace(exercise),
    },
    {
      name: 'Delete',
      color: '#e54d42',
      onClick: () => onRequestDelete(exercise),
    },
  ];

  return readOnly
    ? (
      <S.WorkoutExercise
        readOnly
        number={number}
        topLine={number === prevNumber}
        bottomLine={number === nextNumber}
      >
        <S.Meta>
          <ExerciseOrder
            readOnly
            number={number}
            numbers={numbers}
            exercise={exercise}
            onChange={onRequestSetOrderName}
          />
          <Flex column>
            <Flex>
              <S.Name
                to={`/workout_exercises/${exercise.id}/overview`}
              >
                {exercise.name}
              </S.Name>
              <Alternatives
                readOnly
                exercise={exercise}
              />
            </Flex>
            <Text
              size="12px"
              color="#282d30"
              whiteSpace="pre-wrap"
            >
              {exercise.note || ''}
            </Text>
          </Flex>
        </S.Meta>
        <Flex align="center" spacing={10}>
          <ExerciseProperties
            readOnly
            exerciseId={exercise.id}
            properties={properties}
          />
        </Flex>
      </S.WorkoutExercise>
    )
    : (
      <Draggable
        index={index}
        draggableId={String(exercise.id)}
      >
        {provided => (
          <S.WorkoutExercise
            ref={provided.innerRef}
            {...provided.draggableProps}
            number={number}
            topLine={number === prevNumber}
            bottomLine={number === nextNumber}
          >
            <S.Meta>
              <S.Sort
                {...provided.dragHandleProps}
              >
                <Icon type="fas fa-arrows-alt-v" />
              </S.Sort>

              <ExerciseOrder
                number={number}
                numbers={numbers}
                exercise={exercise}
                onChange={onRequestSetOrderName}
              />

              <Dropdown
                trigger={(
                  <S.ActionsTrigger />
                )}
                items={actions}
              />

              <Flex column grow="1">
                <Flex>
                  <S.Name
                    {...provided.dragHandleProps}
                    to={`/workout_exercises/${exercise.id}/overview`}
                  >
                    {exercise.name}
                  </S.Name>
                  <Alternatives
                    exercise={exercise}
                    onAdd={onRequestAddAlternative}
                    onSet={onRequestSetAlternative}
                    onRemove={onRequestRemoveAlternative}
                  />
                </Flex>

                <S.Note
                  defaultValue={exercise.note || ''}
                  placeholder="Add Note (Exercise Tips, suggestions or exercise specifics)"
                  onSave={(note) => {
                    onRequestUpdate(
                      exercise.id,
                      {
                        note,
                      },
                    );
                  }}
                />
              </Flex>
            </S.Meta>
            <Flex align="center" spacing={10}>
              <ExerciseProperties
                exerciseId={exercise.id}
                properties={properties}
                onRequestSave={onRequestSaveProperty}
                onRequestDelete={onRequestDeleteProperty}
              />

              {visibleSelect && (
                <Checkbox
                  id={exercise.id}
                  isBlink
                  size="large"
                  checked={!!exercise.selected}
                  onChange={() => onSelect(exercise.id, !exercise.selected)}
                />
              )}

              {!visibleSelect && (
                <Flex align="center" noPrint>
                  <Tooltip
                    body={tooltipText.exerciseProperty}
                    preferPlace="left"
                    width="150px"
                  >
                    <S.Add
                      onClick={() => {
                        onRequestAddProperty({
                          position: properties.length + 1,
                          workout_exercise_id: exercise.id,
                        });
                      }}
                    >
                      <S.PlusCircle />
                    </S.Add>
                  </Tooltip>
                </Flex>
              )}
            </Flex>
          </S.WorkoutExercise>
        )}
      </Draggable>
    );
};

export default WorkoutExercise;
